/**
 * The playEngine save, as a queue rather than a timer per edit.
 *
 * WHAT IT REPLACES. Every save posted the entire `playEngineConfig` — the client
 * computed an exact write path and then threw it away, because the route
 * understood nothing else. Here the store's owed paths become dotted `$set`
 * keys, so a one-character text edit sends a few hundred bytes.
 *
 * CADENCE. Persistence is a durability concern, not a rendering one: it wants
 * the fewest requests that still cannot lose work. So a longer debounce than the
 * old 200 ms, a hard ceiling so a long drag still checkpoints, and an explicit
 * flush on the events that actually risk loss — leaving the version, hiding the
 * tab, closing the page.
 *
 * CONCURRENCY. The agent writes this same document from the server. A save
 * carries the revision it read; if the stored one has moved, the server refuses
 * and hands back what it holds, and `rebaseOnServer` replays the paths the other
 * writer left alone. Before this, the two silently overwrote each other.
 */

import _ from "lodash";
import { getServerUri } from "./uriUtility";
import { perfAsync } from "./playEnginePerf";
import {
  beginSave,
  commitSave,
  getBase,
  getRoot,
  rebaseOnServer,
  rollbackSave,
} from "./playEngineDocStore";
import { getIn } from "./setIn";
import { isPersistableFieldPath, safePersistPath } from "./playEnginePersistPath";

/** Quiet time before a save goes out. */
export const PERSIST_DEBOUNCE_MS = 500;
/** Ceiling, so a continuous gesture still checkpoints instead of never saving. */
export const PERSIST_MAX_WAIT_MS = 3000;

/** @type {Map<string, object>} docKey → queue state */
const queues = new Map();

function getQueue(key) {
  if (!queues.has(key)) {
    queues.set(key, {
      key,
      debounceTimer: null,
      maxWaitTimer: null,
      sending: null,
      resendWhenIdle: false,
      conflicts: 0,
      context: {},
    });
  }
  return queues.get(key);
}

function clearTimers(queue) {
  if (queue.debounceTimer) clearTimeout(queue.debounceTimer);
  if (queue.maxWaitTimer) clearTimeout(queue.maxWaitTimer);
  queue.debounceTimer = null;
  queue.maxWaitTimer = null;
}

/**
 * Turn the store's owed paths into a request body.
 *
 * Each path is narrowed to what Mongo can address, which can widen the VALUE
 * being sent (a write inside an opaque map sends the whole map). A path the
 * route would not accept at all — `title`, `lastUpdatingUserID`, anything
 * outside version content — escalates the request to the whole config, which is
 * exactly what used to happen for every save.
 *
 * @param {{ paths: string[], revision: number }} save from docStore.beginSave
 * @param {object} root the live document
 * @returns {{ full: boolean, fields?: object, title?: string, revision: number }}
 */
export function planPersist(save, root) {
  const fields = {};
  let title;

  for (const path of save.paths) {
    if (path === "") return { full: true, revision: save.revision };

    const persistPath = safePersistPath(path);
    if (isPersistableFieldPath(persistPath)) {
      // Read the value off the root rather than reusing the one that was
      // written: a truncated path addresses a wider slice than the edit did.
      fields[persistPath] = getIn(root, persistPath);
      continue;
    }
    if (persistPath === "title") {
      title = _.get(root, "title", "");
      continue;
    }
    return { full: true, revision: save.revision };
  }

  // JSON.stringify drops undefined, so the key would vanish from the body and
  // the write would silently not happen. Send the whole config instead.
  if (Object.values(fields).some((value) => value === undefined)) {
    return { full: true, revision: save.revision };
  }
  if (Object.keys(fields).length === 0 && title === undefined) {
    return { full: true, revision: save.revision };
  }

  return { full: false, fields, title, revision: save.revision };
}

/**
 * Build the urlencoded body for a plan.
 *
 * @param {string} versionID
 * @param {object} plan
 * @param {object} root
 * @returns {URLSearchParams}
 */
export function buildPersistBody(versionID, plan, root) {
  const params = {
    accessToken: localStorage.getItem("session.accessToken"),
    versionID: String(versionID),
  };

  if (plan.full) {
    params.playEngineConfig = JSON.stringify(_.get(root, "playEngineConfig", {}));
    params.dashboardMeta = JSON.stringify(_.get(root, "dashboardMeta", {}));
    params.title = _.get(root, "title", "");
    return new URLSearchParams(params);
  }

  params.fields = JSON.stringify(plan.fields);
  params.configRevision = String(plan.revision);
  if (plan.title !== undefined) params.title = plan.title;
  return new URLSearchParams(params);
}

async function postSave(versionID, plan, root) {
  const response = await fetch(getServerUri("versionPlayEngine/updateOne"), {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: buildPersistBody(versionID, plan, root),
  });

  let body = null;
  try {
    body = await response.json();
  } catch (err) {
    // A plain-text error (401, 403) is not JSON. The status carries the meaning.
  }
  return { status: response.status, body };
}

/**
 * The 409 body names the fields the server holds, not a whole version document.
 * `rebaseOnServer` compares document paths, so it has to be given one.
 */
function serverRootFrom(base, body) {
  return {
    ...base,
    playEngineConfig: _.get(body, "playEngineConfig", {}),
    dashboardMeta: _.get(body, "dashboardMeta", {}),
    configRevision: _.get(body, "configRevision", 0),
  };
}

/**
 * A refusal is only rebasable if it says what to rebase onto. Without a body
 * there is nothing to compare against, and treating the local document as both
 * sides would call every path untouched and retry forever.
 */
function isRebasable(body) {
  return Boolean(body) && _.isPlainObject(body.playEngineConfig);
}

/** Consecutive refusals before we stop retrying and say so. */
const MAX_CONFLICT_RETRIES = 2;

async function send(key) {
  const queue = getQueue(key);
  clearTimers(queue);

  if (queue.sending) {
    // One request at a time; whatever is owed goes out when this one lands.
    queue.resendWhenIdle = true;
    return queue.sending;
  }

  const save = beginSave(key);
  if (!save) return undefined;

  const root = getRoot(key);
  const plan = planPersist(save, root);
  const { onSaved, onConflict, notify } = queue.context;

  queue.sending = (async () => {
    try {
      const { status, body } = await perfAsync("persist:POST updateOne (network)", () =>
        postSave(key, plan, root)
      );

      if (status === 200) {
        queue.conflicts = 0;
        commitSave(key, { revision: _.get(body, "configRevision") });
        if (onSaved) onSaved(getRoot(key));
        return;
      }

      if (status === 409 && isRebasable(body)) {
        queue.conflicts += 1;
        const { replayed, collided } = rebaseOnServer(key, serverRootFrom(getBase(key), body), {
          revision: _.get(body, "configRevision"),
        });
        if (onConflict) onConflict({ replayed, collided, root: getRoot(key) });

        // Retry once against the revision just handed back. Not a loop: a second
        // refusal means writes are landing faster than a round trip, and the
        // honest answer is to hold the edit and say so rather than spin.
        if (replayed.length > 0 && queue.conflicts <= MAX_CONFLICT_RETRIES) {
          queue.resendWhenIdle = true;
        } else if (replayed.length > 0 && notify) {
          notify("This version keeps changing while you edit it — your last change is not saved yet.", "warning");
        }
        return;
      }

      rollbackSave(key);
      if (status === 401) {
        if (notify) notify("You are not authorized to update, contact your administrator.", "warning");
      } else if (notify) {
        notify("Version save failed.", "error");
      }
    } catch (err) {
      // Offline, or the tab is going away. The paths stay owed, so the next
      // flush — or the next edit — carries them.
      rollbackSave(key);
      console.error(err);
    } finally {
      queue.sending = null;
      if (queue.resendWhenIdle) {
        queue.resendWhenIdle = false;
        schedule(key);
      }
    }
  })();

  return queue.sending;
}

/**
 * Note that this document has unsaved work. Repeated calls extend the quiet
 * period up to the ceiling.
 *
 * @param {string} key
 * @param {{ onSaved?: Function, onConflict?: Function, notify?: Function }} [context]
 */
export function schedule(key, context) {
  const queue = getQueue(key);
  if (context) queue.context = { ...queue.context, ...context };

  if (queue.debounceTimer) clearTimeout(queue.debounceTimer);
  queue.debounceTimer = setTimeout(() => {
    queue.debounceTimer = null;
    void send(key);
  }, PERSIST_DEBOUNCE_MS);

  if (!queue.maxWaitTimer) {
    queue.maxWaitTimer = setTimeout(() => {
      queue.maxWaitTimer = null;
      void send(key);
    }, PERSIST_MAX_WAIT_MS);
  }
}

/**
 * Send what is owed now. Used where waiting would risk losing it: leaving the
 * version, hiding the tab, closing the page.
 *
 * Opens the queue if this document has never been scheduled, so a flush cannot
 * silently do nothing for a document that has unsaved work — `send` is the one
 * that decides there is nothing to send.
 *
 * @param {string} key
 * @param {{ onSaved?: Function, onConflict?: Function, notify?: Function }} [context]
 * @returns {Promise|undefined}
 */
export function flush(key, context) {
  const queue = getQueue(key);
  if (context) queue.context = { ...queue.context, ...context };
  return send(key);
}

/** Flush every open document. */
export function flushAll() {
  return Promise.all([...queues.keys()].map((key) => flush(key)));
}

/** True while a request is on the wire — for tests and for a saving indicator. */
export function isSending(key) {
  const queue = queues.get(key);
  return Boolean(queue && queue.sending);
}

/** Drop a document's queue without sending. Its edits stay owed in the store. */
export function cancel(key) {
  const queue = queues.get(key);
  if (!queue) return;
  clearTimers(queue);
  queue.resendWhenIdle = false;
}

/** Tests only. */
export function resetPersistQueue() {
  for (const queue of queues.values()) clearTimers(queue);
  queues.clear();
}
